/** @import "../../../common/types.js" */
import { produce } from 'immer'
import '../../../common/types'
import { BaseControl, BaseFilter, BaseOption, BasicFilterStore } from './common'

export const _size = (obj) => Object.keys(obj).length

class MonthlyMonitorStore extends BasicFilterStore {}

class MonthlyMonitoringFilter extends BaseFilter {
  /**@type {Group[]} */
  #groups = []
  /**@type {Record<string, Control>} */
  #controls = new Map()
  /**@type {Record<string, Option>} */
  #options = new Map()
  /**@type {any[]} */
  #perLevel
  #result

  store = new MonthlyMonitorStore()

  static LAYER_LEVEL_KEY = 'layerLevel'
  static DEPARTMENT_LEVEL_PREFIX = 'departmentLevel'
  static DEPARTMENT_SELECT_PREFIX = 'department'

  constructor(result) {
    super(result)
    this.#result = result
  }

  //フィルターグループの作成
  createGroups() {
    if (_size(this.#perLevel) === 0) {
      this.#groups = []
      return
    }

    this.#groups = [
      {
        title: '表示する階層',
        controlIdList: [],
        disabled: false,
        show: true
      },
      {
        title: '対象の絞り込み',
        controlIdList: [],
        disabled: false,
        show: true,
        hasBorder: false
      }
    ]

    const initialGroups = this.#groups

    //階層Radio
    initialGroups[0].controlIdList = [MonthlyMonitoringFilter.LAYER_LEVEL_KEY]
    initialGroups[1].controlIdList = Array.from(this.#controls.keys()).filter((keys) =>
      keys.includes(MonthlyMonitoringFilter.DEPARTMENT_SELECT_PREFIX)
    )
    return this.#groups
  }

  // デフォルトの階層選択を作成
  createDepartmentLevelOptions(level, departmentLevel) {
    //階層の選択を作成
    const departmentLevelId = `${MonthlyMonitoringFilter.DEPARTMENT_LEVEL_PREFIX}${departmentLevel}`

    const departmentOption = new BaseOption(departmentLevelId, `階層${departmentLevel} `, departmentLevel)
    departmentOption.set('meta', {
      ...departmentOption.meta,
      controlId: MonthlyMonitoringFilter.LAYER_LEVEL_KEY
    })
    departmentOption.set('state', {
      value: false,
      show: _size(level) > 0
    })

    return departmentOption
  }

  // デフォルトの組織
  createDepartmentSelectorOptions(level, levelId) {
    const departmentKey = `${MonthlyMonitoringFilter.DEPARTMENT_SELECT_PREFIX}${levelId}`

    return Object.values(level).map((dept) => {
      const option = new BaseOption(dept.id, dept.name, dept.name)

      option
        .set('info', dept.info)
        .set('meta', {
          level: levelId,
          descendants: dept.descendants,
          refTargets: dept.refTargets,
          controlId: departmentKey
        })
        .set('extra', dept.info)
        .set('state', {
          value: true,
          show: true,
          disabled: false
        })
        .set('onChange', (value) => {
          const { controlState, optionState } = this.store.getStoreData()

          const newOptionState = produce(optionState, (draft) => {
            option.meta.descendants.forEach(([label, level]) => {
              const control = controlState.get(`department${level + 1}`)

              const id = control.optionIdList.find((e) => {
                const option = draft.get(e)
                return option.value === label
              })

              draft.get(id).state.value = value
              draft.get(id).state.show = value
            })
          })

          this.store.overrideOptionState(newOptionState)
        })

      return option
    })
  }

  //フィルターで使う選択の作成
  createOptions() {
    const perLevel = this.#perLevel
    const options = []

    for (let index = 0; index < perLevel.length; index++) {
      const level = perLevel[index]
      const levelId = index + 1
      options.push(this.createDepartmentLevelOptions(level, levelId))
      options.push(...this.createDepartmentSelectorOptions(level, levelId))
    }

    this.#options = new Map(options.map((e) => [e.id, e]))
    return this.#options
  }

  getDepartmentKey(level) {
    return `${MonthlyMonitoringFilter.DEPARTMENT_SELECT_PREFIX}${level}`
  }

  /**
   * @function
   * @param {[string, number][]}  refs
   * @param {Record<string,Control>} controlState
   * @param {Record<string,Option>} optionState
   * @param {function(Control, Option,string):void} cbk
   */
  loopRefs(refs, controlState, optionState, cbk) {
    for (let ref of refs) {
      const control = controlState[this.getDepartmentKey(ref[1] + 1)]

      for (let optionId of control.optionIdList) {
        if (optionState[optionId].label === ref[0]) {
          // current control, current option, optionId
          cbk(control, optionState[optionId], optionId)
        }
      }
    }
  }

  /**
   *
   * @param {Map<string, Control>} controlMap
   */
  createLayerLevel(controlMap) {
    const layerLevelKey = MonthlyMonitoringFilter.LAYER_LEVEL_KEY
    const layerLevelControl = new BaseControl(layerLevelKey, '', 'radio')
    layerLevelControl.state = { show: true, disable: false }
    layerLevelControl.optionIdList = []

    controlMap.set(layerLevelKey, layerLevelControl)
    return layerLevelControl
  }

  createControls() {
    const perLevel = this.#perLevel
    const controlMap = new Map()

    const layerLevelKey = MonthlyMonitoringFilter.LAYER_LEVEL_KEY
    const layerLevelControl = this.createLayerLevel(controlMap)

    for (let index = 0; index < perLevel.length; index++) {
      const level = perLevel[index]
      const levelId = index + 1

      // layerLevel 階層のフィルターキー
      layerLevelControl.optionIdList.push(`${MonthlyMonitoringFilter.DEPARTMENT_LEVEL_PREFIX}${levelId}`)
      // department 組織のフィルターキー
      const departmentKey = `${MonthlyMonitoringFilter.DEPARTMENT_SELECT_PREFIX}${levelId}`

      const control = new BaseControl(departmentKey, `階層${levelId}の組織を選択 `, 'multiselect')
      control.className = 'mt-3'
      control.labelClassName = 'mt-3 mb-2'
      control.state = {
        show: _size(level) > 0,
        disabled: false,
        showDropdown: false,
        filterText: ''
      }

      control.setOptionIdList(Object.values(level).map(((e) => this.#options.get(e.id).id).bind(this)))
      controlMap.set(departmentKey, control)
    }

    //layerLevel値を設定
    const firstOption = controlMap.get(layerLevelKey).optionIdList[0]

    if (firstOption) {
      const layerLevelFirstOption = this.#options.get(firstOption)
      layerLevelFirstOption.state.value = true

      this.#options.set(firstOption, layerLevelFirstOption)
    }

    this.#controls = controlMap
    return this.#controls
  }

  async init() {
    this.#perLevel = await this.processPerLevel(this.#result)

    this.createOptions()
    this.createControls()
    this.createGroups()
  }

  getConfig() {
    return {
      groups: this.#groups,
      controls: this.#controls,
      options: this.#options,
      generateOutput: this.generateOutput
    }
  }
}

export default MonthlyMonitoringFilter
